import config from '@/config';

import { useMemo, useState } from 'react';

import { json } from '@codemirror/lang-json';
import { oneDark } from '@codemirror/theme-one-dark';
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Toolbar, Typography } from '@mui/material';
import CodeMirror from '@uiw/react-codemirror';
import { useParams } from 'react-router';

import { useSnackbar } from '@/components/feedback/SnackbarProvider';

import { useRecipeEditor } from '../context/RecipeEditorContext';
import { useToolbarActions } from '../hooks/useToolbarActions';

interface EventContextPanelProps {
  tenantId: string;
  environments: Array<{ id: string; name: string }>;
  tenantKey: string;
  appUniqueKey: string;
}

/**
 * Event Context editor panel matching legacy EditTransConsole in eventContext mode.
 * Shows a JSON editor pre-populated with default event data, and a "Start Recipe" button.
 */
export const EventContextPanel: React.FC<EventContextPanelProps> = ({
  environments,
  tenantKey,
  appUniqueKey,
}) => {
  const { appId: _appId } = useParams<{ appId: string }>();
  const { state } = useRecipeEditor();
  const { selectedEnvironment, isExecuting } = state;
  const { showSnackbar } = useSnackbar();

  const toolbarActions = useToolbarActions({
    environments,
    tenantKey,
    appUniqueKey,
    onError: (message) => showSnackbar(message, 'error'),
    onSuccess: (message) => showSnackbar(message, 'success'),
  });

  const [jsonError, setJsonError] = useState<string | null>(null);

  // Build default event data matching legacy (edittrans.js startRecipeDirect)
  const defaultEventData = useMemo(() => {
    const ftpDir = config.FTP_DIR || 'organizations/';
    const fullPath = `/${ftpDir}${tenantKey}/${appUniqueKey}/sample.zip`;
    const selectedEnv = environments.find((env) => env.id === selectedEnvironment);

    const data: Record<string, string> = {
      action: 'create',
      fullPath,
      eventDate: new Date().toISOString(),
      type: 'file',
    };

    if (selectedEnv) {
      data.environmentName = selectedEnv.name;
      data.environmentId = String(selectedEnv.id);
    }

    return data;
  }, [tenantKey, appUniqueKey, environments, selectedEnvironment]);

  const [editorContent, setEditorContent] = useState(() =>
    JSON.stringify(defaultEventData, null, 2)
  );

  const handleStartRecipe = () => {
    setJsonError(null);

    let parsed: Record<string, string>;
    try {
      parsed = JSON.parse(editorContent);
    } catch (error) {
      setJsonError(
        `There's an issue with your Event Context JSON formatting.\n\n${error instanceof Error ? error.message : String(error)}`
      );
      return;
    }

    // Execute via the toolbar actions helper
    toolbarActions.executeFromEventContext(parsed);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header with Start Recipe button */}
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 40,
          borderBottom: '1px solid',
          borderColor: 'divider',
          gap: 1,
        }}
      >
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={
            isExecuting ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />
          }
          disabled={isExecuting || !selectedEnvironment}
          onClick={handleStartRecipe}
        >
          Start Recipe
        </Button>
        <Typography variant="body2" color="text.secondary">
          Edit the event context JSON below, then click Start Recipe to execute.
        </Typography>
      </Toolbar>

      {/* JSON validation error */}
      {jsonError && (
        <Alert severity="error" onClose={() => setJsonError(null)} sx={{ mx: 1, mt: 1 }}>
          {jsonError}
        </Alert>
      )}

      {/* CodeMirror JSON editor */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <CodeMirror
          value={editorContent}
          height="100%"
          extensions={[json()]}
          theme={oneDark}
          onChange={(value) => setEditorContent(value)}
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            autocompletion: true,
            bracketMatching: true,
            closeBrackets: true,
            indentOnInput: true,
          }}
          editable={!isExecuting}
        />
      </Box>
    </Box>
  );
};
